/**
 * Responsive Filter Chip Component
 *
 * Shows dropdown on desktop (sm+), bottom sheet on mobile.
 * Used for search filter chips that need better mobile UX.
 */

import { SM_BREAKPOINT } from './breakpoints.js';

export function responsiveFilterChip(filterId) {
    return {
        filterId,
        dropdownOpen: false,
        sheetOpen: false,
        dirty: false,

        /**
         * Dynamically check viewport width (evaluated on each access)
         */
        get isDesktop() {
            return window.innerWidth >= SM_BREAKPOINT;
        },

        /**
         * Move the single content node into the target container
         */
        moveContent(target) {
            const src = this.$refs.filterContent;
            if (!src || !target) return;
            // Move all children from source into target
            while (src.firstChild) {
                target.appendChild(src.firstChild);
            }
        },

        /**
         * Return content back to the hidden source container
         */
        returnContent() {
            const src = this.$refs.filterContent;
            const sheet = document.getElementById(this.filterId + '-sheet-content');
            const dropdown = this.$refs.dropdownContent;
            if (!src) return;
            // Move content back from whichever container has it
            for (const container of [sheet, dropdown]) {
                if (container) {
                    while (container.firstChild) {
                        src.appendChild(container.firstChild);
                    }
                }
            }
        },

        /**
         * Handle chip click - open dropdown on desktop, sheet on mobile
         */
        handleClick(event) {
            if (event) {
                event.stopPropagation();
            }

            // Close any other open filters first
            this.closeOtherFilters();

            if (this.isDesktop) {
                // Desktop: toggle dropdown
                this.dropdownOpen = !this.dropdownOpen;
                this.sheetOpen = false;

                // Initialize Lucide icons when opening
                if (this.dropdownOpen) {
                    this.dirty = false;
                    this.returnContent();
                    this.$nextTick(() => {
                        this.moveContent(this.$refs.dropdownContent);
                        if (window.LucideIcons) {
                            window.LucideIcons.initialize(this.$el);
                        }
                    });
                }
            } else {
                // Mobile: open bottom sheet (scroll lock handled by $watch in template)
                this.sheetOpen = true;
                this.dropdownOpen = false;
                this.dirty = false;

                this.returnContent();
                // Move content to sheet after teleport renders
                this.$nextTick(() => {
                    this.moveContent(document.getElementById(this.filterId + '-sheet-content'));
                    if (window.LucideIcons) {
                        window.LucideIcons.initialize();
                    }
                });
            }
        },

        /**
         * Mark filter as dirty (called from DOM events)
         */
        markDirty() {
            this.dirty = true;
        },

        /**
         * Close the bottom sheet
         */
        closeSheet() {
            this.sheetOpen = false;
            this.dropdownOpen = false;
            this.returnContent();
            // Scroll unlock handled by $watch in template
        },

        /**
         * Apply filters and submit the form.
         * Hides the sheet immediately (no animation) to avoid a blank content flash,
         * then shows skeleton loaders at the results level during page navigation.
         */
        applyAndClose() {
            // Hide the entire sheet overlay instantly (skip Alpine leave transitions)
            // to prevent the blank content flash while DOM nodes are moved back to the form
            const sheetContent = document.getElementById(this.filterId + '-sheet-content');
            if (sheetContent) {
                // Walk up to the teleported root (outermost fixed overlay)
                const overlay = sheetContent.closest('.fixed.inset-0');
                if (overlay) overlay.style.display = 'none';
            }
            this.sheetOpen = false;
            this.dropdownOpen = false;
            this.returnContent();
            window.scrollLock?.unlock();

            // Show loading skeletons at results level
            const viewMode = document.querySelector('input[name="viewMode"]')?.value || 'list';
            if (window.showSearchLoadingState) {
                window.showSearchLoadingState(viewMode);
            }

            // Submit the search form
            const form = document.querySelector('.search-form');
            if (form) {
                form.submit();
            }
        },

        /**
         * Close other open filter dropdowns/sheets
         */
        closeOtherFilters() {
            // Dispatch a custom event that other filter instances can listen to
            document.dispatchEvent(new CustomEvent('close-filter-dropdowns', {
                detail: { except: this.filterId }
            }));
        },

        /**
         * Initialize the component
         */
        init() {
            // Return content when desktop dropdown closes (click outside, escape, etc.)
            this.$watch('dropdownOpen', (open) => {
                if (!open) {
                    this.returnContent();
                }
            });

            // Listen for close events from other filters
            document.addEventListener('close-filter-dropdowns', (e) => {
                if (e.detail.except !== this.filterId) {
                    this.dropdownOpen = false;
                    // Also close sheet if another filter is opening
                    if (this.sheetOpen) {
                        this.closeSheet();
                    }
                }
            });

            // Handle window resize - close sheet if viewport becomes desktop
            window.addEventListener('resize', () => {
                if (this.sheetOpen && this.isDesktop) {
                    this.closeSheet();
                }
            });
        }
    };
}
